import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import Dropdown from '@cartwhl/ui/atoms/Dropdown';
import Image from '@cartwhl/ui/atoms/Image';
import Input from '@cartwhl/ui/atoms/Input';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import DownvoteIcon from '@cartwhl/ui/icons/DownvoteIcon';
import ExpandIcon from '@cartwhl/ui/icons/ExpandIcon';
import HistoryIcon from '@cartwhl/ui/icons/HistoryIcon';
import ThreeDotsIcon from '@cartwhl/ui/icons/ThreeDotsIcon';
import UpvoteIcon from '@cartwhl/ui/icons/UpvoteIcon';
import Button from '@cartwhl/ui/input/Button';

import { noticesStore, posingStore, projectStore, uiStore } from 'stores';

import RenderFeedbackModal from './RenderFeedbackModal';
import RenderViewModal from './RenderViewModal';

export default function RenderPanelContent() {
  const {
    renderedPoseURL,
    lastRenderPrompt,
    posingSystem,
    renderingInProgress,
  } = useSnapshot(posingStore.state);
  const [prompt, setPrompt] = useState(lastRenderPrompt || '');
  const [isRenderViewOpen, setIsRenderViewOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [feedbackSentiment, setFeedbackSentiment] = useState<
    'positive' | 'negative'
  >('positive');

  useEffect(() => {
    if ((lastRenderPrompt || '') !== prompt) {
      setPrompt(lastRenderPrompt || '');
    }
  }, [lastRenderPrompt]);

  const handleDownload = () => {
    if (!renderedPoseURL) return;
    const link = document.createElement('a');
    link.href = renderedPoseURL;
    link.download = 'rendered-pose.png';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.dispatchEvent(new MouseEvent('click'));
  };

  const handleExpand = () => {
    setIsRenderViewOpen(true);
  };

  const openFeedback = (sentiment: 'positive' | 'negative') => {
    setFeedbackSentiment(sentiment);
    setIsFeedbackOpen(true);
  };

  const handleChangePrompt = (e: any) => {
    const value = e.target.value;
    setPrompt(value);
    posingStore.state.lastRenderPrompt = value;
  };

  const handleRender = async () => {
    if (!posingSystem.current) {
      noticesStore.info(
        'An error has occurred, please refresh the page and try again.',
      );
      return;
    }
    if (!projectStore.state.currentProject) {
      noticesStore.info(
        'Try changing the pose before doing your first render.',
      );
      return;
    }

    const canvasImageTaskURL = await posingSystem.current.canvasImageTask({
      showAxelClothes: true,
      // let's keep playing with this
      // showDome: true,
      // showGrid: true,
    });
    const canvasBase64 = canvasImageTaskURL.replace(
      /^data:image\/\w+;base64,/,
      '',
    );
    posingStore.setLastCanvasBase64(canvasBase64);

    if (!projectStore.state.currentProject.data?.projectID) {
      noticesStore.info(
        'An error has occurred, please refresh the page and try again.',
      );
      return;
    }

    await posingStore.renderPose(
      projectStore.state.currentProject.data.projectID,
      posingStore.state.lastRenderPrompt,
      canvasBase64,
    );
  };

  const imageURL = renderedPoseURL || '/images/pose_tailor.webp';

  return (
    <div className="flex min-w-fit flex-col gap-2">
      <div className="bg-gray-15 dark:bg-gray-75 relative w-[360px] rounded-lg">
        <Image
          src={imageURL}
          crossOrigin="anonymous"
          alt="Render"
          className="aspect-4/3 w-full cursor-pointer rounded-[inherit] object-cover"
          onClick={handleExpand}
        />
        <div className="absolute top-2 left-2">
          <div className="flex gap-2">
            <Tooltip text="Download image">
              <Button
                size="sm"
                variant="secondary"
                icon={<DownloadIcon />}
                onClick={handleDownload}
              />
            </Tooltip>

            <Tooltip text="Expand image">
              <Button
                size="sm"
                variant="secondary"
                icon={<ExpandIcon />}
                onClick={handleExpand}
              />
            </Tooltip>
          </div>
        </div>
        <div className="absolute top-2 right-2">
          <Dropdown
            trigger={
              <Button size="sm" variant="secondary" icon={<ThreeDotsIcon />} />
            }
          >
            <Dropdown.Item onClick={() => uiStore.setOpenPanel('history')}>
              <div className="flex items-center gap-2">
                <HistoryIcon /> Render history
              </div>
            </Dropdown.Item>
            <Dropdown.Separator />
            <Dropdown.Item onClick={() => openFeedback('positive')}>
              <div className="flex items-center gap-2">
                <UpvoteIcon /> Report great
              </div>
            </Dropdown.Item>
            <Dropdown.Item onClick={() => openFeedback('negative')}>
              <div className="flex items-center gap-2">
                <DownvoteIcon /> Report issue
              </div>
            </Dropdown.Item>
          </Dropdown>
        </div>
      </div>
      <div className="bg-gray-15 dark:bg-gray-75 hover:bg-gray-30 dark:hover:bg-gray-60 flex flex-col items-end rounded-lg transition-colors">
        <Input
          name="render"
          as="textarea"
          placeholder="Describe what you want the character and scene to look like..."
          value={prompt}
          onChange={handleChangePrompt}
          autoResize
          autoResizeOptions={{ minHeight: 68, maxHeight: 240 }}
          className="bg-transparent dark:bg-transparent"
        />
        <Button
          variant="primary"
          size="sm"
          loading={renderingInProgress}
          onClick={handleRender}
          className="mr-2 mb-2"
        >
          Render
        </Button>
      </div>

      <RenderViewModal
        open={isRenderViewOpen}
        onOpenChange={setIsRenderViewOpen}
        renderedPoseURL={imageURL}
      />

      {isFeedbackOpen && (
        <RenderFeedbackModal
          open={isFeedbackOpen}
          onOpenChange={setIsFeedbackOpen}
          sentiment={feedbackSentiment}
          prompt={lastRenderPrompt}
          imageURL={imageURL}
        />
      )}
    </div>
  );
}
